import type { Ref } from 'react';
import { requestForQuoteProductName } from '~/utils/requestForQuoteProductName';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  InfiniteScrollTable,
  type TableColumn,
  type TableRow,
} from '~/components/ui/table';
import { statusBadgeClasses } from '~/components/ui/statusBadgeClasses';
import { formatDate } from '~/utils';
import { QuoteStatus, type MyQuotesQuery } from '~/graphql/graphql';

export type MyQuoteRow = MyQuotesQuery['myQuotes']['content'][number];

type MyQuotesListProps = {
  quotes: MyQuoteRow[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  sentinelRef?: Ref<HTMLDivElement>;
  isLoadingMore?: boolean;
  hasMoreData?: boolean | null;
  totalCount?: number;
};

function statusBadgeClassName(status: QuoteStatus): string {
  switch (status) {
    case QuoteStatus.Accepted:
      return `${statusBadgeClasses('accepted')} whitespace-nowrap`;
    case QuoteStatus.Submitted:
      return `${statusBadgeClasses('info')} whitespace-nowrap`;
    case QuoteStatus.Declined:
      return `${statusBadgeClasses('declined')} whitespace-nowrap`;
    case QuoteStatus.Archived:
      return `${statusBadgeClasses('neutral')} whitespace-nowrap`;
    default:
      return `${statusBadgeClasses('pending')} whitespace-nowrap`;
  }
}

export function MyQuotesList({
  quotes,
  isLoading,
  isError,
  onRetry,
  sentinelRef,
  isLoadingMore,
  hasMoreData,
  totalCount,
}: Readonly<MyQuotesListProps>) {
  const { t } = useTranslation('my-quotes');
  const navigate = useNavigate();

  const columns: TableColumn[] = [
    {
      key: 'buyingOrganization',
      header: t('my_quotes.columns.buying_organization'),
      width: 16,
      stickTo: 'left',
    },
    { key: 'product', header: t('my_quotes.columns.product'), width: 16 },
    { key: 'status', header: t('my_quotes.columns.status'), width: 8 },
    { key: 'createdAt', header: t('my_quotes.columns.created_at'), width: 8 },
    {
      key: 'actions',
      header: t('my_quotes.columns.actions'),
      width: 8,
      justification: 'end',
      stickTo: 'right',
    },
  ];

  const rows: TableRow[] = quotes.map((quote) => ({
    id: { value: quote.id },
    buyingOrganization: {
      value: quote.requestForQuote.buyingOrganization.name,
    },
    product: {
      value: requestForQuoteProductName(quote.requestForQuote) ?? '-',
    },
    status: {
      value: (
        <span className={statusBadgeClassName(quote.status)}>
          {t(`my_quotes.status.${quote.status}`)}
        </span>
      ),
    },
    createdAt: { value: formatDate(quote.createdAt) },
    actions: {
      value: (
        <button
          type="button"
          onClick={() => navigate(`/seller-quotes/view/${quote.id}`)}
          data-testid={`my-quote-view-${quote.id}`}
          className="px-4 py-2 text-sm font-medium text-prim border border-prim rounded-lg hover:bg-emerald-50 transition-colors whitespace-nowrap"
        >
          {quote.needsSellerResponse
            ? t('my_quotes.respond')
            : t('my_quotes.view')}
        </button>
      ),
    },
  }));

  return (
    <InfiniteScrollTable
      columns={columns}
      rows={rows}
      isLoading={isLoading}
      isError={isError}
      loadingName={t('my_quotes.title')}
      errorMessage={t('my_quotes.load_error')}
      errorTestId="my-quotes-error"
      onRetry={onRetry}
      emptyStateTitle={t('my_quotes.no_quotes_title')}
      emptyStateMessage={t('my_quotes.no_quotes_message')}
      tableTestId="my-quotes-table"
      tableName={t('my_quotes.items')}
      loadingMoreLabel={t('my_quotes.loading_more')}
      noMoreItemsLabel={t('my_quotes.no_more_items')}
      totalCount={totalCount}
      isLoadingMore={isLoadingMore}
      hasMoreData={hasMoreData}
      sentinelRef={sentinelRef}
    />
  );
}
